import {
    Button,
    ButtonGroup,
    CheckBox,
    Control,
    Icon,
    Label,
    NumberBox,
    Pagination,
    Panel,
    TextArea,
    TextBox,
} from "@core/components/alt-ui/controls";
import {
    IGood,
    IGoodInventory,
    IGoodInventoryCreateDto,
    IGoodInventoryItem,
    IGoodInventoryItemReportResult,
    IGoodInventoryUpdateDto,
    IGoodWriteOff,
    IGoodWriteOffCreateDto,
    ISelectedData,
    IStore,
    Locale,
} from "@lib";
import { Table, TableColumn, TableFooter } from "@core/components/alt-ui/table";
import { DelayedOperation } from "@/utils/delayed-operation";
import { Uuid } from "@/utils/uuid";
import { Modal } from "@core/components/alt-ui/modal";
import { SearchGoods } from "@/core/alt-forms/components/search-goods/search-goods";
import { GoodWriteOffModal, IGoodWriteOffTableItem } from "../../good-write-offs/good-write-off.modal";
import router from "@/router/router";
import { Formatter } from "@/utils/formatter";
import { AltMessage } from "@/utils/plugins/alt-message";
import { AltToast } from "@/utils/plugins/alt-toast";

export interface IGoodInventoryModalContext {
    userId: string;
    store?: IStore;
    message: AltMessage;
    toast: AltToast;

    /** Инвентаризация для редактирования. */
    inventory: IGoodInventory;

    /** Обработчик создания инвентаризации. */
    createHandler?: (dto: IGoodInventoryCreateDto) => Promise<IGoodInventory | null>;

    /** Обработчик изменения инвентаризации. */
    updateHandler?: (orig: IGoodInventory, dto: IGoodInventoryUpdateDto) => Promise<IGoodInventory | null>;

    /** Обработчик возобновления инвентаризации. */
    resumeHandler?: (inventory: IGoodInventory) => Promise<IGoodInventory | null>;

    /** Обработчик поиска товаров. */
    searchGoodsHandler?: (search: string, storeId: string) => Promise<ISelectedData<IGood>>;

    /** Обработчик загрузки товаров. */
    loadItemsHandler?: (
        inventory: IGoodInventory,
        options?: IGoodInventoryLoadItemsOptions,
    ) => Promise<ISelectedData<IGoodInventoryItem> | null>;

    /** Обработчик загрузки товаров для списания. */
    loadItemsForWriteOffHandler?: (inventory: IGoodInventory) => Promise<IGoodInventoryItem[] | null>;

    /** Обработчик загрузки информации об инвентаризации. */
    loadReportHandler?: (inventory: IGoodInventory) => Promise<IGoodInventoryItemReportResult | null>;

    /** Обработчик заполнения таблицы нулевыми товарами. */
    populateItemsHandler?: (inventory: IGoodInventory) => Promise<boolean>;

    /** Обработчик добавления товара в инвентаризацию. */
    addGoodHandler?: (inventory: IGoodInventory, good: IGood, quantity: number) => Promise<IGoodInventoryItem | null>;

    /** Обработчик обновления количества товара в инвентаризации. */
    updateItemQuantityHandler?: (
        inventory: IGoodInventory,
        itemId: string,
        quantity: number,
    ) => Promise<IGoodInventoryItem | null>;

    /** Обработчик удаления товара из инвентаризации. */
    deleteItemHandler?: (inventory: IGoodInventory, itemId: string) => Promise<boolean>;

    /** Обработчик загрузки списаний инвентаризации */
    loadWriteOffsHandler?: (inventory: IGoodInventory) => Promise<IGoodWriteOff[]>;

    /** Обработчик создания списания. */
    createWriteOffHandler?: (dto: IGoodWriteOffCreateDto) => Promise<IGoodWriteOff | null>;
}

export enum GoodInventoryLoadItemsSort {
    Name,
    Date,
}

export interface IGoodInventoryLoadItemsOptions {
    limit?: number;
    offset?: number;
    diff?: boolean;
    sort?: GoodInventoryLoadItemsSort;
}

export class GoodInventoryModal extends Modal<IGoodInventoryModalContext, boolean> {
    private tbStore!: TextBox;
    private ctSearchGoods!: SearchGoods;
    private chbDiff!: CheckBox;
    private pgPagination!: Pagination;
    private lbPagination!: Label;
    private btnSortDate!: Button;
    private btnSortName!: Button;
    private btngSort!: ButtonGroup;
    private tblTools!: Panel;
    private tblItemsTable!: Table<IGoodInventoryItem>;
    private taComment!: TextArea;

    private btnCancel!: Button;
    private btnComplete!: Button;
    private btnWriteOff!: Button;
    private pnlFooter!: Panel;

    private context?: IGoodInventoryModalContext;

    private items: IGoodInventoryItem[] = [];
    private itemsTotal = 0;
    private itemsPage = 1;
    private itemsLimit = 20;
    private itemsSort = GoodInventoryLoadItemsSort.Date;

    private itemsSurplus = 0;
    private itemsShortage = 0;

    private delay = 700;

    private writeoffs: IGoodWriteOff[] = [];
    private writeOffModal = new GoodWriteOffModal("good-inventory-writeoff-modal");

    public constructor(id = "good-inventory-modal") {
        super(id, "");
        this.size = "lg";
        //this.scrollable = false;
        this.initializeControls();
    }

    public get controls(): Control[] {
        return [
            this.tbStore,
            this.ctSearchGoods,
            this.chbDiff,
            this.tblTools,
            this.tblItemsTable,
            this.taComment,
            this.pnlFooter,
        ];
    }

    public get submodals(): Modal<any, any>[] {
        return [this.writeOffModal];
    }

    public async show(context: IGoodInventoryModalContext): Promise<boolean> {
        this.context = context;

        this.itemsSort = this.context.inventory.done
            ? GoodInventoryLoadItemsSort.Name
            : GoodInventoryLoadItemsSort.Date;

        this.initializeControls();

        await Promise.all([this.loadItems(), this.loadWriteOffs()]);
        this.populateControls(this.context.inventory);

        return super.show();
    }

    private initializeControls(): void {
        this.tbStore = new TextBox();
        this.tbStore.id = "good-inventory.store";
        this.tbStore.label = "Склад";
        this.tbStore.disabled = true;

        this.ctSearchGoods = new SearchGoods();
        this.ctSearchGoods.id = "good-inventory.good-search";
        this.ctSearchGoods.label = "Введите название товара";
        this.ctSearchGoods.selectSingle = true;
        this.ctSearchGoods.searchFunc = search => this.searchGoods(search);
        this.ctSearchGoods.addSelectItemHandler(async (s, e) => {
            if (!e.item) {
                return;
            }

            await this.addItem(e.item, 1);
        });

        this.chbDiff = new CheckBox();
        this.chbDiff.id = "good-inventory.good-diff";
        this.chbDiff.text = "Показать только отклонения";
        this.chbDiff.class = "mt-0.5";
        this.chbDiff.value = this.context?.inventory.done ?? false;
        this.chbDiff.addValueChangedHandler((s, e) => this.loadItems());

        this.pgPagination = new Pagination();
        this.pgPagination.class = "mr-0.5";
        this.pgPagination.limit = this.itemsLimit;
        this.pgPagination.addChangePageHandler((s, e) => this.changePage(e.page));

        this.lbPagination = new Label();
        this.lbPagination.class = "text-base mr-0.5";

        this.btnSortDate = new Button();
        this.btnSortDate.variant = this.itemsSort === GoodInventoryLoadItemsSort.Date ? "primary" : "outline-secondary";
        this.btnSortDate.text = "Добавл.";
        this.btnSortDate.addClickHandler(() => this.changeSort(GoodInventoryLoadItemsSort.Date));

        this.btnSortName = new Button();
        this.btnSortName.variant = this.itemsSort === GoodInventoryLoadItemsSort.Name ? "primary" : "outline-secondary";
        this.btnSortName.text = "Наимен.";
        this.btnSortName.addClickHandler(() => this.changeSort(GoodInventoryLoadItemsSort.Name));

        this.btngSort = new ButtonGroup();
        this.btngSort.size = "sm";
        this.btngSort.class = "mr-0.5";
        this.btngSort.addButtons([this.btnSortDate, this.btnSortName]);

        this.tblTools = new Panel();
        this.tblTools.class = "flex items-center justify-between mt-1";
        this.tblTools.addControls([this.btngSort, this.lbPagination, this.pgPagination]);

        this.tblItemsTable = new Table<IGoodInventoryItem>();
        this.tblItemsTable.id = "good-inventory.good-table";
        this.tblItemsTable.columns = this.getTableColumns(false);

        this.taComment = new TextArea();
        this.taComment.id = "good-inventory.comment";
        this.taComment.label = "Комментарий";
        this.taComment.addTextChangedHandler((s, e) => this.updateComment(e.text));

        this.btnCancel = new Button();
        this.btnCancel.id = "good-inventory.cancel";
        this.btnCancel.text = "Закрыть";
        this.btnCancel.variant = "outline-danger";
        this.btnCancel.addClickHandler(() => this.clickCancel());

        this.btnComplete = new Button();
        this.btnComplete.id = "good-inventory.complete";
        this.btnComplete.text = "Завершить";
        this.btnComplete.addClickHandler(() => this.clickComplete());

        this.btnWriteOff = new Button();
        this.btnWriteOff.id = "good-inventory.write-off";
        this.btnWriteOff.variant = "outline-primary";
        this.btnWriteOff.class = "ml-0.5";
        this.btnWriteOff.text = "Списать";
        this.btnWriteOff.visible = false;
        this.btnWriteOff.addClickHandler(() => this.clickWriteOff());

        const pnlFooterLeft = new Panel();
        pnlFooterLeft.class = "flex";
        pnlFooterLeft.addControls([this.btnComplete, this.btnWriteOff]);
        //pnlFooterLeft.addControls([this.btnComplete]);

        this.pnlFooter = new Panel();
        this.pnlFooter.class = "flex justify-between mt-2";
        this.pnlFooter.addControls([pnlFooterLeft, this.btnCancel]);
    }

    private populateControls(inventory: IGoodInventory): void {
        this.title = `Инвентаризация #${inventory.number}`;

        this.tbStore.text = this.context?.store ? this.context.store.info.name : "";

        this.ctSearchGoods.visible = true;

        this.pgPagination.page = this.itemsPage;
        this.pgPagination.limit = this.itemsLimit;
        this.pgPagination.total = this.itemsTotal;

        this.tblItemsTable.items = this.getTableItems();
        this.tblItemsTable.columns = this.getTableColumns(inventory.done);
        this.tblItemsTable.footer = this.getTableFooter();

        this.taComment.text = inventory.comment ?? "";
        this.taComment.disabled = false;

        this.btnComplete.text = "Завершить";
        this.btnComplete.visible = true;

        this.btnWriteOff.visible = false;

        if (inventory.done) {
            this.title += ` - ${Formatter.datetime(inventory.doneAt)}`;
            this.btnComplete.text = "Возобновить";
            this.btnWriteOff.visible = true;
            this.taComment.disabled = true;
            this.ctSearchGoods.visible = false;
        }
    }

    private getTableItems(): IGoodInventoryItem[] {
        return this.items;
    }

    private getTableFooter(): TableFooter<IGoodInventoryItem>[] {
        const locale = this.context?.store?.info.locale ?? Locale.RU;

        const footer: TableFooter<IGoodInventoryItem>[] = [
            {
                title: "Излишек:",
                cell: items => {
                    return Formatter.money(this.itemsSurplus, {
                        locale: locale,
                        grouping: true,
                        showNullFraction: false,
                    });
                },
            },
            {
                title: "Недостача:",
                cell: items => {
                    return Formatter.money(this.itemsShortage, {
                        locale: locale,
                        grouping: true,
                        showNullFraction: false,
                    });
                },
            },
        ];

        if (this.writeoffs.length > 0) {
            footer.push({
                title: "Списано:",
                cell: items => {
                    let writtenOffCost = 0;
                    for (const writeoff of this.writeoffs) {
                        for (const good of writeoff.goods) {
                            writtenOffCost += good.cost * good.writedOffQuantity;
                        }
                    }

                    return Formatter.money(writtenOffCost, {
                        locale: locale,
                        grouping: true,
                        showNullFraction: false,
                    });
                },
            });
        }

        return footer;
    }

    private getTableColumns(readonly: boolean): TableColumn<IGoodInventoryItem>[] {
        const columns: TableColumn<IGoodInventoryItem>[] = [
            {
                title: "Наименование",
                width: 50,
                classHeader: "text-left text-xs",
                classCell: "text-left",
                cell: item => {
                    const group = new Panel();
                    group.class = "flex";

                    const name = new Label();
                    name.class = "text-base text-overflow-dots";
                    name.style = "max-width: 25rem;";
                    name.text = item.name;

                    group.addControl(name);

                    if (item.good) {
                        const icon = new Icon();
                        icon.class = "cursor-pointer text-secondary hover:text-dark w-1 ml-0.75";
                        icon.icon = "ExternalLinkIcon";
                        icon.addClickHandler((s, e) => {
                            const routeData = router.resolve({ name: "stores", query: { id: item.good } });
                            window.open(routeData.href, "_blank");
                        });

                        group.addControl(icon);
                    }

                    return group;
                },
            },
            {
                title: "Факт",
                classHeader: "text-xs",
                width: 20,
                cell: item => {
                    if (readonly) {
                        const lbl = new Label();
                        lbl.id = Uuid.new();
                        lbl.class = "text-base";
                        lbl.text = item.inventoryQuantity.toString();
                        return lbl;
                    }

                    const number = new NumberBox();
                    number.id = Uuid.new();
                    number.style = "min-width: 9rem;";
                    number.min = 0;
                    number.max = Number.MAX_VALUE;
                    number.value = item.inventoryQuantity;
                    number.disabled = readonly;
                    number.addValueChangedHandler((s, e) => {
                        if (e.value === null) {
                            return;
                        }

                        this.updateItemQuantity(item.id, e.value);
                    });

                    return number;
                },
            },
            {
                title: "Учёт",
                classHeader: "text-xs",
                width: 20,
                cell: item => item.quantity.toString(),
            },
            {
                title: "Отклонение",
                classHeader: "text-xs",
                width: 20,
                cell: item => {
                    const delta = item.inventoryQuantity - item.quantity;
                    const color = delta > 0 ? "text-success" : delta < 0 ? "text-danger" : "";

                    const lbl = new Label();
                    lbl.id = Uuid.new();
                    lbl.class = `text-base ${color}`;
                    lbl.text = delta.toString();
                    return lbl;
                },
            },
        ];

        if (!readonly) {
            columns.unshift({
                width: 1,
                cell: (item, index) => {
                    const iconDelete = new Icon();
                    iconDelete.icon = "Trash2Icon";
                    iconDelete.class = "cursor-pointer hover:text-danger";
                    iconDelete.addClickHandler(async () => await this.deleteItem(item, index));
                    return iconDelete;
                },
            });
        }

        if (this.writeoffs.length > 0) {
            columns.push({
                title: "Списано",
                classHeader: "text-xs",
                width: 20,
                cell: item => {
                    const writtenoffQuantity = item.good ? this.getWrittenOffQuantity(item.good) : 0;

                    const lbl = new Label();
                    lbl.class = "text-base";
                    lbl.text = writtenoffQuantity === 0 ? "" : writtenoffQuantity.toString();
                    return lbl;
                },
            });
        }

        columns.push({
            title: "Цена",
            classHeader: "text-xs",
            width: 20,
            cell: item =>
                Formatter.money(item.cost, {
                    locale: this.context?.store?.info.locale ?? Locale.RU,
                    grouping: true,
                    showNullFraction: false,
                }),
        });

        return columns;
    }

    private getWrittenOffQuantity(goodId: string): number {
        let writtenoffQuantity = 0;

        for (const writeoff of this.writeoffs) {
            const good = writeoff.goods.find(g => g.id === goodId);

            if (good) {
                writtenoffQuantity += good.writedOffQuantity;
            }
        }

        return writtenoffQuantity;
    }

    private searchGoods(search: string): Promise<ISelectedData<IGood>> {
        if (!this.context || !this.context.searchGoodsHandler || !this.context?.store) {
            return Promise.resolve({ data: [], total: 0 });
        }

        const storeId = this.context.store.id;
        return this.context.searchGoodsHandler(search, storeId);
    }

    private changeSort(sort: GoodInventoryLoadItemsSort): Promise<void> {
        if (this.itemsSort === sort) {
            return Promise.resolve();
        }

        this.itemsSort = sort;

        this.btnSortDate.variant = "outline-secondary";
        this.btnSortName.variant = "outline-secondary";

        if (sort === GoodInventoryLoadItemsSort.Date) {
            this.btnSortDate.variant = "primary";
        }

        if (sort === GoodInventoryLoadItemsSort.Name) {
            this.btnSortName.variant = "primary";
        }

        return this.loadItems(1);
    }

    private changePage(page: number): Promise<void> {
        return this.loadItems(page);
    }

    private updatePaginationText(): void {
        const offset = this.itemsLimit * (this.itemsPage - 1);
        const first = offset + 1;
        const last = Math.min(offset + this.itemsLimit, this.itemsTotal);
        this.lbPagination.text = `${first} - ${last} из ${this.itemsTotal}`;
    }

    private async loadItems(page?: number): Promise<void> {
        if (!this.context?.loadItemsHandler || !this.context?.loadReportHandler) {
            return;
        }

        if (page === undefined) {
            page = this.itemsSort === GoodInventoryLoadItemsSort.Date ? 1 : this.itemsPage;
        }

        const options: IGoodInventoryLoadItemsOptions = {
            limit: this.itemsLimit,
            offset: this.itemsLimit * (page - 1),
            diff: this.chbDiff.value,
            sort: this.itemsSort,
        };

        const result = await this.context.loadItemsHandler(this.context.inventory, options);

        if (!result) {
            return;
        }

        this.items = result.data;
        this.itemsTotal = result.total;
        this.itemsPage = page;
        this.updatePaginationText();

        const report = await this.context.loadReportHandler(this.context.inventory);

        if (!report) {
            return;
        }

        this.itemsSurplus = report.surplus;
        this.itemsShortage = report.shortage;

        //this.initializeControls();
        this.populateControls(this.context.inventory);
    }

    private async addItem(good: IGood, quantity: number): Promise<void> {
        if (!this.context?.addGoodHandler) {
            return;
        }

        const item = await this.context.addGoodHandler(this.context.inventory, good, quantity);

        if (!item) {
            return;
        }

        await this.loadItems();
    }

    private updateItemQuantity(itemId: string, quantity: number): void {
        if (!this.context?.updateItemQuantityHandler) {
            return;
        }

        const inventory = this.context.inventory;
        const updateItemQuantity = this.context.updateItemQuantityHandler;

        DelayedOperation.invoke(`inventory-update-item-quantity-${itemId}`, this.delay, async () => {
            const item = await updateItemQuantity(inventory, itemId, quantity);

            if (!item) {
                return;
            }

            await this.loadItems();
        });
    }

    private async deleteItem(item: IGoodInventoryItem, index: number): Promise<void> {
        if (!this.context?.deleteItemHandler) {
            return;
        }

        const confirm = await this.context.message.confirm(
            `Вы уверены, что хотите удалить товар "${item.name}"?`,
            "Удаление товара",
            { acceptText: "Удалить" },
        );

        if (!confirm) {
            return;
        }

        const deleted = await this.context.deleteItemHandler(this.context.inventory, item.id);

        if (!deleted) {
            return;
        }

        await this.loadItems();
    }

    private async loadWriteOffs(): Promise<void> {
        if (!this.context?.loadWriteOffsHandler) {
            return;
        }

        this.writeoffs = await this.context.loadWriteOffsHandler(this.context.inventory);
    }

    private updateComment(comment: string): void {
        if (!this.context?.updateHandler || this.context.inventory.comment === comment) {
            return;
        }

        const inventory = this.context.inventory;
        const updateInventory = this.context.updateHandler;

        DelayedOperation.invoke("inventory-update-comment", this.delay, async () => {
            await updateInventory(inventory, { comment });
            inventory.comment = comment;
        });
    }

    private async clickCancel(): Promise<void> {
        this.hide(false);
    }

    private async clickWriteOff(): Promise<void> {
        if (!this.context?.loadItemsForWriteOffHandler) {
            return;
        }

        const items = await this.context.loadItemsForWriteOffHandler(this.context.inventory);

        if (!items || items.length === 0) {
            this.context.toast.warning("В инвентаризации нет товаров для списания.");
            return;
        }

        const writeoffItems = items.map(i => {
            // уже списанное количество
            const writtenoffQuantity = i.good ? this.getWrittenOffQuantity(i.good) : 0;

            return {
                goodId: i.good,
                name: i.name,
                cost: i.cost,
                quantity: i.quantity,
                writtenOffQuantity: i.quantity - i.inventoryQuantity - writtenoffQuantity,
            } as IGoodWriteOffTableItem;
        });

        const writeoff = await this.writeOffModal.show({
            userId: this.context.userId,
            stores: this.context.store ? [this.context.store] : [],
            storeId: this.context.store ? this.context.store.id : "",
            message: this.context.message,
            toast: this.context.toast,
            fromInventory: {
                inventoryId: this.context.inventory.id,
                storeId: this.context.inventory.store,
                items: writeoffItems,
                comment: `Списание на основе инвентаризации #${this.context.inventory.number}`,
            },
            createHandler: this.context.createWriteOffHandler,
        });

        if (writeoff) {
            if (!this.context.inventory.writeOffs) {
                this.context.inventory.writeOffs = [];
            }
            this.context.inventory.writeOffs.push(writeoff.id);

            this.writeoffs.push(writeoff);
            this.populateControls(this.context.inventory);
        }
    }

    private async clickComplete(): Promise<void> {
        if (!this.context || !this.tblItemsTable.items.length) {
            return;
        }

        const valid = await this.validate();

        if (!valid) {
            return;
        }

        const inventory = this.context.inventory?.done ? await this.resume() : await this.complete();

        if (inventory) {
            this.context.inventory = inventory;
            this.populateControls(inventory);
        }
    }

    private async complete(): Promise<IGoodInventory | null> {
        if (!this.context?.updateHandler) {
            return null;
        }

        const dto: IGoodInventoryUpdateDto = {
            comment: this.taComment.text,
            done: true,
        };

        return await this.context.updateHandler(this.context.inventory, dto);
    }

    private async resume(): Promise<IGoodInventory | null> {
        if (!this.context?.resumeHandler || !this.context.inventory) {
            return null;
        }

        return await this.context.resumeHandler(this.context.inventory);
    }
}
